import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import type { PageProps } from "@/types/inertia"

/**
 * Duo does not send a time zone, and every automation schedule has to be shown
 * in one. So the first time a signed-in user loads a page without one, the
 * browser's own zone is saved quietly in the background.
 *
 * It writes once and never again: the server refuses to overwrite a zone that
 * is already set, so a deliberate choice on the settings page cannot be undone
 * by signing in from a laptop in another state.
 */
export function TimezoneDetector() {
  const { props } = usePage<PageProps>()
  const user = props.current_user
  const needsZone = Boolean(user) && !user?.timezone
  const asked = React.useRef(false)

  React.useEffect(() => {
    // Intl is read in an effect, not during render: this component is rendered
    // during SSR too, where there is no browser zone to report.
    if (!needsZone || asked.current) return

    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!zone) return

    asked.current = true
    router.patch(
      "/settings/timezone",
      { timezone: zone },
      { preserveScroll: true, preserveState: true },
    )
  }, [needsZone])

  return null
}
